/**
 * Cómo se nombra un paquete (VTA-01). Random cuenta el clavo y la tienda vende
 * la caja de 100: el precio, el stock y la cantidad del carro ya llegan en
 * paquetes, y lo único que falta es decirlo, en todos lados igual. El correo
 * de confirmación usa el mismo texto (`backend/src/utilidades/paquete.ts`).
 */
import type { UnidadMedida } from '@elcarpintero/compartido';

const NOMBRE: Record<UnidadMedida, { singular: string; plural: string }> = {
  unidad: { singular: 'unidad', plural: 'unidades' },
  metro: { singular: 'metro', plural: 'metros' },
  kilo: { singular: 'kilo', plural: 'kilos' },
};

/** Hasta dos decimales: un tornillo de la caja de 300 cuesta $12,40. */
const PESOS_PIEZA = new Intl.NumberFormat('es-CL', {
  style: 'currency',
  currency: 'CLP',
  maximumFractionDigits: 2,
});

const NUMERO = new Intl.NumberFormat('es-CL');

interface ConPaquete {
  unidadesPorPaquete: number;
  unidadMedida: UnidadMedida;
}

export function esPaquete(producto: ConPaquete): boolean {
  return producto.unidadesPorPaquete > 1;
}

/** "Paquete de 100 unidades", o `null` si se vende suelto. */
export function textoPaquete(producto: ConPaquete): string | null {
  if (!esPaquete(producto)) return null;
  return `Paquete de ${NUMERO.format(producto.unidadesPorPaquete)} ${NOMBRE[producto.unidadMedida].plural}`;
}

/** "unidad", "metro" o "kilo": en qué cuenta Random la pieza. */
export function nombrePieza(producto: ConPaquete): string {
  return NOMBRE[producto.unidadMedida].singular;
}

/**
 * "$131,61": el precio de referencia de la pieza, para comparar con quien
 * vende suelto. Solo se muestra; lo que se cobra es el paquete.
 */
export function precioPieza(producto: ConPaquete & { precio: number }): string {
  const pieza = producto.precio / producto.unidadesPorPaquete;
  return PESOS_PIEZA.format(pieza >= 100 ? Math.round(pieza) : pieza);
}

/** "300 unidades": cuántas piezas son `paquetes` paquetes. */
export function textoPiezas(producto: ConPaquete, paquetes: number): string {
  const piezas = paquetes * producto.unidadesPorPaquete;
  const nombre = NOMBRE[producto.unidadMedida];
  return `${NUMERO.format(piezas)} ${piezas === 1 ? nombre.singular : nombre.plural}`;
}

/** "3 paquetes disponibles" o "3 unidades disponibles", según se venda. */
export function textoDisponibles(producto: ConPaquete, cantidad: number): string {
  const [singular, plural] = esPaquete(producto) ? ['paquete', 'paquetes'] : ['unidad', 'unidades'];
  return `${NUMERO.format(cantidad)} ${cantidad === 1 ? singular : plural}`;
}
